import { Directive, ElementRef, EventEmitter, Input, Output } from '@angular/core';

import {
  animationFrameScheduler,
  concat as observableConcat,
  Observable,
  ReplaySubject,
  Subject,
} from 'rxjs';
import { filter, finalize, first, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

import { LifeCycle } from '@recruitee/utils';

export type ObservedEntry = { target: HTMLElement; width: number; height: number };

const observerSubject = new Subject<ObservedEntry>();
const resizeObserver$ = observerSubject.pipe(shareReplay(1, undefined, animationFrameScheduler));
const observer = (<any>window).ResizeObserver
  ? new ResizeObserver(entries => {
      for (const entry of entries) {
        observerSubject.next({
          target: <HTMLElement>entry.target,
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    })
  : new (class FakeResizeObserver {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      public observe() {}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      public unobserve() {}
    })();

@Directive({
  selector: '[rtResizeObserver]',
  exportAs: 'resizeObserver',
})
export class ResizeObserverEntryDirective {
  @Input('rtResizeObserver') public opts: {
    initial: boolean;
  } = {
    initial: false,
  };

  @Output() public resized = new EventEmitter<ObservedEntry>();
  public get entry(): ObservedEntry | undefined {
    if (this.lastValue === undefined) {
      const bbox = this.elRef.nativeElement.getBoundingClientRect();
      return {
        target: this.elRef.nativeElement,
        width: bbox.width,
        height: bbox.height,
      };
    }

    return this.lastValue;
  }

  private sizeSubject$ = new ReplaySubject<ObservedEntry>(1);
  private lastValue: any;

  @LifeCycle('onInit')
  private onInit$: Subject<void>;

  @LifeCycle('onDestroy')
  private onDestroy$: Subject<void>;

  @LifeCycle('afterViewInit')
  private afterViewInit$: Subject<void>;

  constructor(private elRef: ElementRef) {
    const streams = [];

    if (this.opts.initial) {
      const initial$: Observable<ObservedEntry> = this.afterViewInit$.pipe(
        map(() => {
          const { width, height } = this.elRef.nativeElement.getBoundingClientRect();
          return {
            target: this.elRef.nativeElement,
            width,
            height,
          };
        }),
        first(),
      );
      streams.push(initial$);
    }

    const resize$ = resizeObserver$.pipe(
      filter((entry: ObservedEntry) => entry.target === this.elRef.nativeElement),
    );

    streams.push(resize$);

    this.onInit$.subscribe(() => observer.observe(this.elRef.nativeElement));

    this.sizeSubject$
      .asObservable()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(entry => this.resized.emit(entry));

    observableConcat(...streams)
      .pipe(
        tap(value => (this.lastValue = value)),
        finalize(() => observer.unobserve(this.elRef.nativeElement)),
        takeUntil(this.onDestroy$),
      )
      .subscribe((entry: ObservedEntry) => this.sizeSubject$.next(entry));
  }
}
